import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, CheckCircle2, Flag, LogOut, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { PoliticsQuestionCard, PoliticsSolution } from "@/components/politics-question";
import { Button, EmptyState, LoadingLine } from "@/components/primitives";
import { ApiError, apiFetch } from "@/lib/api";
import {
  normalizedOptionKeys,
  politicsAnswerErrorDisposition,
  type PoliticsAnswerResult,
  type PoliticsOptionKey,
  type PoliticsQuestionPrompt,
  type PoliticsSession,
  type PoliticsSessionProgress,
} from "@/lib/politics";

interface AnswerResponse {
  result: PoliticsAnswerResult;
  sessionProgress: PoliticsSessionProgress;
  nextItem: { id: string; queueOrder: number; question: PoliticsQuestionPrompt } | null;
}

function updateFavorite(session: PoliticsSession, questionId: string, favorite: boolean): PoliticsSession {
  return {
    ...session,
    currentItem: session.currentItem?.question.id === questionId
      ? { ...session.currentItem, question: { ...session.currentItem.question, favorite } }
      : session.currentItem,
    items: session.items.map((item) => item.question?.id === questionId
      ? { ...item, question: { ...item.question, favorite } }
      : item),
  };
}

export function PoliticsPracticePage() {
  const { sessionId } = useParams({ from: "/app/$subject/practice/$sessionId" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<PoliticsOptionKey[]>([]);
  const [feedback, setFeedback] = useState<AnswerResponse | null>(null);
  const [notice, setNotice] = useState("");
  const [confirmAbandon, setConfirmAbandon] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const sessionQuery = useQuery({
    queryKey: ["politics", "session", sessionId],
    queryFn: () => apiFetch<{ session: PoliticsSession }>(`/politics/sessions/${sessionId}`),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const session = sessionQuery.data?.session;
  const item = session?.currentItem ?? null;
  useEffect(() => {
    setSelected([]);
    setFeedback(null);
    setNotice("");
  }, [item?.id]);

  const answer = useMutation({
    mutationFn: () => {
      if (!item) throw new Error("no_current_item");
      return apiFetch<AnswerResponse>(`/politics/sessions/${sessionId}/items/${item.id}/answer`, {
        method: "POST",
        body: JSON.stringify({ selectedOptionKeys: normalizedOptionKeys(selected) }),
      });
    },
    onSuccess: (response) => {
      setFeedback(response);
      const invalidations = [
        queryClient.invalidateQueries({ queryKey: ["politics", "session", sessionId], refetchType: "none" }),
        queryClient.invalidateQueries({ queryKey: ["politics", "dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["politics", "wrong"] }),
        queryClient.invalidateQueries({ queryKey: ["politics", "history"] }),
        queryClient.invalidateQueries({ queryKey: ["politics", "favorites"] }),
      ];
      if (item) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: ["politics", "question", item.question.id] }),
        );
      }
      void Promise.all(invalidations);
    },
    onError: async (error) => {
      if (
        error instanceof ApiError
        && politicsAnswerErrorDisposition(error.message) === "synchronize"
      ) {
        setSelected([]);
        setFeedback(null);
        const synchronizedNotice = error.message === "answer_already_submitted"
          ? "这道题已在其他页面提交，已同步服务器上的最新进度。"
          : "练习进度已经变化，已同步到最新题目。";
        await sessionQuery.refetch();
        setNotice(synchronizedNotice);
        return;
      }
      setNotice("答案提交失败，请保留当前页面后重试。");
    },
  });

  const setFavorite = async (favorite: boolean) => {
    if (!item || favoriteBusy) return;
    setNotice("");
    setFavoriteBusy(true);
    try {
      await apiFetch(`/politics/questions/${item.question.id}/favorite`, { method: favorite ? "PUT" : "DELETE" });
      queryClient.setQueryData<{ session: PoliticsSession }>(
        ["politics", "session", sessionId],
        (current) => current ? { session: updateFavorite(current.session, item.question.id, favorite) } : current,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["politics", "favorites"] }),
        queryClient.invalidateQueries({ queryKey: ["politics", "dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["politics", "question", item.question.id] }),
      ]);
    } catch {
      setNotice("收藏状态保存失败，请稍后重试。");
    } finally {
      setFavoriteBusy(false);
    }
  };

  const continueSession = async () => {
    if (!feedback) return;
    if (!feedback.nextItem || feedback.sessionProgress.completed) {
      queryClient.removeQueries({ queryKey: ["politics", "session", sessionId] });
      await navigate({
        to: "/app/$subject/history/$sessionId",
        params: { subject: "politics", sessionId },
      });
      return;
    }
    setFeedback(null);
    setSelected([]);
    await sessionQuery.refetch();
  };

  const abandon = async () => {
    setNotice("");
    try {
      await apiFetch(`/politics/sessions/${sessionId}/abandon`, { method: "POST" });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["politics"] }),
        navigate({ to: "/app/$subject/history", params: { subject: "politics" } }),
      ]);
    } catch {
      setConfirmAbandon(false);
      setNotice("结束练习失败，当前进度仍已保留。");
    }
  };

  if (sessionQuery.isLoading) return <div className="page"><LoadingLine /></div>;
  if (sessionQuery.isError || !session) {
    return (
      <div className="page">
        <EmptyState
          title="没有找到这组练习"
          description="它可能已经被删除，或不属于当前账号。"
          action={<Link className="button button--primary button--md" to="/app/$subject/question-bank" params={{ subject: "politics" }}>返回题库</Link>}
        />
      </div>
    );
  }
  if (session.status !== "active" || !item) {
    const completed = session.status === "completed";
    return (
      <div className="page politics-session-complete">
        <CheckCircle2 />
        <p className="section-label">{completed ? "本组练习已完成" : "本组练习已结束"}</p>
        <h1>{session.correctCount} / {completed ? session.actualCount : session.answeredCount} 题正确</h1>
        <p>{completed ? "本次作答已经保存，可以在答题记录中逐题回看。" : "已经提交的答案仍然保留，未作答题目不计入成绩。"}</p>
        <div>
          <Link className="button button--primary button--lg" to="/app/$subject/history/$sessionId" params={{ subject: "politics", sessionId }}>查看本次记录</Link>
          <Link className="button button--secondary button--lg" to="/app/$subject/question-bank" params={{ subject: "politics" }}>继续练习</Link>
        </div>
      </div>
    );
  }

  const answeredCount = feedback?.sessionProgress.answeredCount ?? session.answeredCount;
  const totalCount = feedback?.sessionProgress.actualCount ?? session.actualCount;
  const progress = totalCount ? Math.round((answeredCount / totalCount) * 100) : 0;
  return (
    <div className="page politics-practice-page">
      <header className="politics-practice-toolbar">
        <Link to="/app/$subject/question-bank" params={{ subject: "politics" }}><ArrowLeft /> 返回题库</Link>
        <div className="politics-practice-progress">
          <span><strong>{Math.min(answeredCount + (feedback ? 0 : 1), totalCount)}</strong> / {totalCount}</span>
          <div><i style={{ width: `${progress}%` }} /></div>
        </div>
        {session.status === "active" ? <Button variant="ghost" size="sm" type="button" onClick={() => setConfirmAbandon(true)}><LogOut /> 结束练习</Button> : null}
      </header>
      <main className="politics-practice-stage">
        <PoliticsQuestionCard
          question={item.question}
          selected={selected}
          result={feedback?.result}
          disabled={answer.isPending}
          favoriteBusy={favoriteBusy}
          onSelectionChange={setSelected}
          onFavoriteChange={(favorite) => void setFavorite(favorite)}
        />
        {feedback ? <PoliticsSolution solution={feedback.result} result={feedback.result} /> : null}
        {notice ? <p className="politics-inline-notice" role="status">{notice}</p> : null}
      </main>
      <footer className="politics-practice-actions">
        <span>{item.question.type === "multiple_choice" ? "多选题：请选择所有正确选项" : "单选题：请选择一个最合适的选项"}</span>
        {feedback ? (
          <Button type="button" variant="primary" size="lg" onClick={() => void continueSession()}>
            {feedback.nextItem && !feedback.sessionProgress.completed ? <>下一题 <ArrowRight /></> : <>完成练习 <Flag /></>}
          </Button>
        ) : (
          <Button type="button" variant="primary" size="lg" disabled={selected.length === 0 || answer.isPending} onClick={() => answer.mutate()}>
            <Send /> {answer.isPending ? "正在提交…" : "提交答案"}
          </Button>
        )}
      </footer>
      {confirmAbandon ? (
        <div className="politics-dialog-backdrop" role="presentation" onMouseDown={() => setConfirmAbandon(false)}>
          <section className="politics-dialog" role="alertdialog" aria-modal="true" aria-labelledby="abandon-politics-title" onMouseDown={(event) => event.stopPropagation()}>
            <p className="section-label">结束本组练习</p>
            <h2 id="abandon-politics-title">确定不再继续本组题目吗？</h2>
            <p>已经提交的答案会保留，尚未作答的题目不会计入成绩。</p>
            <div className="politics-dialog__actions">
              <Button type="button" onClick={() => setConfirmAbandon(false)}>继续答题</Button>
              <Button type="button" variant="danger" onClick={() => void abandon()}>确认结束</Button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
